// Printale Premiere Wall — interaction module
const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const isCoarsePointer = window.matchMedia('(pointer: coarse)');

function initSection(section) {
  if (section.dataset.premiereInit === 'true') return;
  section.dataset.premiereInit = 'true';

  initReveals(section);
  initSpotlight(section);
  initCardTilt(section);
  initPosterGlow(section);
  initVelvetRope(section);
  initModal(section);
}

/* Scroll-in reveals for [data-premiere-reveal] */
function initReveals(section) {
  const targets = section.querySelectorAll('[data-premiere-reveal]');
  if (!('IntersectionObserver' in window)) {
    targets.forEach((el) => el.classList.add('is-visible'));
    return;
  }
  const io = new IntersectionObserver(
    (entries, obs) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          obs.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.18 }
  );
  targets.forEach((el) => io.observe(el));
}

/* Global cursor spotlight — sets --pw-mx / --pw-my on the section */
function initSpotlight(section) {
  if (prefersReducedMotion.matches || isCoarsePointer.matches) return;
  let raf = null;
  let tx = 50;
  let ty = 30;
  let cx = 50;
  let cy = 30;

  section.addEventListener('pointermove', (e) => {
    const rect = section.getBoundingClientRect();
    tx = ((e.clientX - rect.left) / rect.width) * 100;
    ty = ((e.clientY - rect.top) / rect.height) * 100;
    if (!raf) raf = requestAnimationFrame(tick);
  });

  function tick() {
    cx += (tx - cx) * 0.12;
    cy += (ty - cy) * 0.12;
    section.style.setProperty('--pw-mx', cx.toFixed(2) + '%');
    section.style.setProperty('--pw-my', cy.toFixed(2) + '%');
    if (Math.abs(tx - cx) > 0.1 || Math.abs(ty - cy) > 0.1) {
      raf = requestAnimationFrame(tick);
    } else {
      raf = null;
    }
  }
}

/* Per-card tilt toward cursor */
function initCardTilt(section) {
  const tiltEnabled = !prefersReducedMotion.matches && !isCoarsePointer.matches;
  const cards = section.querySelectorAll('[data-premiere-card]');

  cards.forEach((card) => {
    if (tiltEnabled) {
      card.addEventListener('pointermove', (e) => onCardMove(e, card));
      card.addEventListener('pointerenter', () => card.classList.add('is-tilting'));
      card.addEventListener('pointerleave', () => resetCard(card));
    }
  });
}

const MAX_RX = 6; // deg
const MAX_RY = 8; // deg

function onCardMove(e, card) {
  const rect = card.getBoundingClientRect();
  const px = (e.clientX - rect.left) / rect.width; // 0..1
  const py = (e.clientY - rect.top) / rect.height; // 0..1
  const ry = (px - 0.5) * 2 * MAX_RY; // left/right
  const rx = (0.5 - py) * 2 * MAX_RX; // up/down
  card.style.setProperty('--ry', ry.toFixed(2) + 'deg');
  card.style.setProperty('--rx', rx.toFixed(2) + 'deg');
  card.style.setProperty('--gx', (px * 100).toFixed(1) + '%');
  card.style.setProperty('--gy', (py * 100).toFixed(1) + '%');
}

function resetCard(card) {
  card.classList.remove('is-tilting');
  card.style.setProperty('--ry', '0deg');
  card.style.setProperty('--rx', '0deg');
  card.style.setProperty('--gx', '50%');
  card.style.setProperty('--gy', '50%');
}

function initPosterGlow(section) {
  const cards = section.querySelectorAll('[data-premiere-card]');

  cards.forEach((card) => {
    card.addEventListener('pointerenter', () => {
      const styles = getComputedStyle(card);
      section.style.setProperty('--pw-poster-r', styles.getPropertyValue('--card-glow-r').trim() || '244');
      section.style.setProperty('--pw-poster-g', styles.getPropertyValue('--card-glow-g').trim() || '199');
      section.style.setProperty('--pw-poster-b', styles.getPropertyValue('--card-glow-b').trim() || '110');
      section.classList.add('is-poster-lit');
    });

    card.addEventListener('pointerleave', () => {
      section.classList.remove('is-poster-lit');
    });
  });
}

async function initVelvetRope(section) {
  const mount = section.querySelector('[data-premiere-rope-stage]');
  if (!mount || prefersReducedMotion.matches) return;

  try {
    const THREE = await import('three');
    const renderer = new THREE.WebGLRenderer({
      alpha: true,
      antialias: true,
      powerPreference: 'low-power',
    });
    renderer.setClearColor(0x000000, 0);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.75));
    mount.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(34, 1, 0.1, 100);
    camera.position.set(0, 1.45, 8.4);
    camera.lookAt(0, 0.1, 0);

    const warmLight = new THREE.PointLight(0xffd18a, 2.1, 12);
    warmLight.position.set(-3.2, 3.4, 4.2);
    scene.add(warmLight);

    const fillLight = new THREE.PointLight(0xff8fa8, 0.9, 10);
    fillLight.position.set(3.2, 2.4, 3.2);
    scene.add(fillLight);

    scene.add(new THREE.AmbientLight(0x4a1018, 1.15));

    const ropeMaterial = new THREE.MeshStandardMaterial({
      color: 0x8f1024,
      roughness: 0.72,
      metalness: 0.02,
      emissive: 0x180006,
      emissiveIntensity: 0.18,
    });
    const ropeHighlight = new THREE.MeshStandardMaterial({
      color: 0xf05a63,
      roughness: 0.52,
      metalness: 0.02,
      emissive: 0x2b0208,
      emissiveIntensity: 0.18,
    });
    const brassMaterial = new THREE.MeshStandardMaterial({
      color: 0xd6a84f,
      roughness: 0.36,
      metalness: 0.72,
      emissive: 0x2e1904,
      emissiveIntensity: 0.08,
    });
    const shadowMaterial = new THREE.MeshBasicMaterial({
      color: 0x000000,
      transparent: true,
      opacity: 0.26,
      depthWrite: false,
    });

    const group = new THREE.Group();
    group.position.y = -0.62;
    scene.add(group);

    const postPositions = [-4.6, -2.2, 0, 2.2, 4.6];
    const postGeometry = new THREE.CylinderGeometry(0.075, 0.095, 1.22, 22);
    const knobGeometry = new THREE.SphereGeometry(0.19, 24, 14);
    const baseGeometry = new THREE.CylinderGeometry(0.26, 0.34, 0.12, 28);

    postPositions.forEach((x, index) => {
      const post = new THREE.Mesh(postGeometry, brassMaterial);
      post.position.set(x, -0.06, index % 2 ? -0.18 : 0);
      group.add(post);

      const knob = new THREE.Mesh(knobGeometry, brassMaterial);
      knob.position.set(x, 0.58, index % 2 ? -0.18 : 0);
      knob.scale.set(1, 0.82, 1);
      group.add(knob);

      const base = new THREE.Mesh(baseGeometry, brassMaterial);
      base.position.set(x, -0.72, index % 2 ? -0.18 : 0);
      group.add(base);
    });

    for (let index = 0; index < postPositions.length - 1; index += 1) {
      const x1 = postPositions[index];
      const x2 = postPositions[index + 1];
      const z = index % 2 ? -0.2 : 0;
      const curve = new THREE.CatmullRomCurve3([
        new THREE.Vector3(x1, 0.48, z),
        new THREE.Vector3((x1 + x2) * 0.5, -0.12 - (index % 2) * 0.08, z + 0.16),
        new THREE.Vector3(x2, 0.48, z),
      ]);
      const rope = new THREE.Mesh(new THREE.TubeGeometry(curve, 44, 0.085, 18, false), ropeMaterial);
      group.add(rope);

      const highlightCurve = new THREE.CatmullRomCurve3([
        new THREE.Vector3(x1, 0.55, z + 0.025),
        new THREE.Vector3((x1 + x2) * 0.5, -0.03 - (index % 2) * 0.08, z + 0.19),
        new THREE.Vector3(x2, 0.55, z + 0.025),
      ]);
      const highlight = new THREE.Mesh(new THREE.TubeGeometry(highlightCurve, 36, 0.022, 10, false), ropeHighlight);
      group.add(highlight);
    }

    const floorShadow = new THREE.Mesh(new THREE.CircleGeometry(4.8, 56), shadowMaterial);
    floorShadow.position.set(0, -0.78, -0.18);
    floorShadow.rotation.x = -Math.PI / 2;
    floorShadow.scale.set(1, 0.18, 1);
    group.add(floorShadow);

    let width = 0;
    let height = 0;
    let frame = 0;
    let visible = true;
    const resize = () => {
      const rect = mount.getBoundingClientRect();
      width = Math.max(1, Math.round(rect.width));
      height = Math.max(1, Math.round(rect.height));
      renderer.setSize(width, height, false);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
    };

    const observer = 'IntersectionObserver' in window
      ? new IntersectionObserver((entries) => {
        visible = entries.some((entry) => entry.isIntersecting);
        if (visible && !frame) frame = requestAnimationFrame(render);
      }, { threshold: 0.01 })
      : null;
    if (observer) observer.observe(mount);

    function render(time) {
      resize();
      group.rotation.y = Math.sin(time * 0.00025) * 0.035;
      group.position.x = Math.sin(time * 0.00018) * 0.08;
      renderer.render(scene, camera);
      frame = visible ? requestAnimationFrame(render) : 0;
    }

    window.addEventListener('resize', resize, { passive: true });
    resize();
    frame = requestAnimationFrame(render);
  } catch (error) {
    mount.hidden = true;
  }
}

/* Review modal: open from card data, focus-trap, Esc to close */
function initModal(section) {
  const modal = section.querySelector('[data-premiere-modal]');
  if (!modal) return;

  const els = {
    image: modal.querySelector('[data-premiere-modal-image]'),
    badge: modal.querySelector('[data-premiere-modal-badge]'),
    occasion: modal.querySelector('[data-premiere-modal-occasion]'),
    title: modal.querySelector('[data-premiere-modal-title]'),
    name: modal.querySelector('[data-premiere-modal-name]'),
    stars: modal.querySelector('[data-premiere-modal-stars]'),
    quote: modal.querySelector('[data-premiere-modal-quote]'),
  };
  let lastFocused = null;

  section.querySelectorAll('[data-premiere-card]').forEach((card) => {
    card.addEventListener('click', () => openModal(card));
  });

  modal.querySelectorAll('[data-premiere-modal-close]').forEach((el) => {
    el.addEventListener('click', closeModal);
  });

  document.addEventListener('keydown', (e) => {
    if (modal.hasAttribute('hidden')) return;
    if (e.key === 'Escape') closeModal();
    if (e.key === 'Tab') trapFocus(e);
  });

  function openModal(card) {
    lastFocused = card;
    const d = card.dataset;
    const rating = parseInt(d.rating, 10) || 5;
    if (d.image) {
      els.image.src = d.image;
      els.image.alt = d.name + ' — Printale poster';
      els.image.closest('.premiere-modal__poster').style.display = '';
    } else {
      els.image.closest('.premiere-modal__poster').style.display = 'none';
    }
    els.badge.textContent = d.badge || '';
    els.occasion.textContent = d.occasion || '';
    els.title.textContent = d.title || '';
    els.name.textContent = d.name || '';
    els.stars.textContent = '★★★★★☆☆☆☆☆'.slice(5 - rating, 10 - rating);
    els.quote.textContent = d.quote ? '"' + d.quote + '"' : '';

    modal.removeAttribute('hidden');
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('premiere-modal-open');
    requestAnimationFrame(() => modal.classList.add('is-open'));
    modal.querySelector('.premiere-modal__close').focus();
  }

  function closeModal() {
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('premiere-modal-open');
    const onEnd = () => {
      modal.setAttribute('hidden', '');
      modal.removeEventListener('transitionend', onEnd);
    };
    modal.addEventListener('transitionend', onEnd);
    if (lastFocused) lastFocused.focus();
  }

  function trapFocus(e) {
    const focusables = modal.querySelectorAll('button, a[href], img[tabindex], [tabindex]:not([tabindex="-1"])');
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }
}

function boot() {
  document.querySelectorAll('[data-premiere-wall]').forEach(initSection);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}

// Shopify theme-editor support
document.addEventListener('shopify:section:load', boot);

export { initSection };
